import React from "react";
import { useNavigate } from "react-router-dom";
import { FaShip, FaAnchor } from "react-icons/fa";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import Select from "react-select";

const YachtFleetHeader = ({ title, link, onPrev, onNext, yachts, onFilterChange }) => {
  const navigate = useNavigate();

  const handleSeeMore = () => {
    // Navigate to a general yacht page or specific category page
    // Pass the category title as state or query param
    navigate('/yachts', {
      state: {
        selectedCategory: [title], // Pass the title directly as category
        // Reset other filters if needed
      }
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getFilterOptions = (yachts) => {
    const options = {
      guests: new Set(),
      cabins: new Set(),
      length: new Set(),
      year: new Set(),
    };

    yachts.forEach((yacht) => {
      if (yacht.guestCapacity) options.guests.add(yacht.guestCapacity);
      if (yacht.cabins) options.cabins.add(yacht.cabins);
      if (yacht.specifications?.length) options.length.add(parseFloat(yacht.specifications.length));
      if (yacht.yearBuilt) options.year.add(yacht.yearBuilt);
    });

    const sortedGuests = Array.from(options.guests).sort((a, b) => a - b);
    const sortedCabins = Array.from(options.cabins).sort((a, b) => a - b);
    const sortedYears = Array.from(options.year).sort((a, b) => b - a);

    return {
      guests: sortedGuests.map((g) => ({
        value: g,
        label: `Up to ${g} Guests`,
      })),
      cabins: sortedCabins.map((c) => ({
        value: c,
        label: `${c} Cabins`,
      })),
      length: Array.from(options.length).sort((a, b) => a - b).map((l) => ({
        value: l,
        label: `${l}m Length`,
      })),
      year: sortedYears.map((y) => ({
        value: y,
        label: `Built ${y}`,
      })),
    };
  };

  const filterOptions = getFilterOptions(yachts);

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      minHeight: "42px",
      borderColor: "#d1d5db",
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#9ca3af',
      }
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? "#e5e7eb" : "white",
      color: "#1f2937",
      "&:hover": {
        backgroundColor: "#f3f4f6",
      },
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#dbeafe',
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#1e3a8a',
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#1e3a8a',
      ':hover': {
        backgroundColor: '#bfdbfe',
        color: '#1e40af',
      },
    }),
  };

  return (
    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8 px-4 md:px-0">
      {/* Title and See More Button */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full md:w-auto">
        <h2 className="JosefinSans text-2xl font-semibold text-blue-900">{title}</h2>
      </div>

      {/* Filter and Navigation */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full md:w-auto">
        <Select
          isMulti
          placeholder="Filter yachts..."
          className="w-full sm:w-64 text-sm"
          options={[
            { label: "Capacity", options: filterOptions.guests },
            { label: "Accommodation", options: filterOptions.cabins },
            { label: "Size & Year", options: [...filterOptions.length, ...filterOptions.year] },
          ]}
          onChange={onFilterChange}
          styles={customSelectStyles}
        />
        <div className="flex justify-end gap-2">
          <button
            onClick={onPrev}
            aria-label={`Previous ${title}`}
            className="bg-white p-3 rounded-md border border-gray-300 text-gray-500 hover:bg-blue-900 hover:text-white transition-all flex-1 sm:flex-none shadow-sm hover:shadow-md"
          >
            <FaChevronLeft />
          </button>
          <button
            onClick={onNext}
            aria-label={`Next ${title}`}
            className="bg-white p-3 rounded-md border border-gray-300 text-gray-500 hover:bg-blue-900 hover:text-white transition-all flex-1 sm:flex-none shadow-sm hover:shadow-md"
          >
            <FaChevronRight />
          </button>
        </div>
      </div>
    </div>
  );
};

export default YachtFleetHeader;
